import { createFileRoute, Link } from "@tanstack/react-router";

import { SectionHeading } from "@/components/section-heading";
import { GoLiveChecklist } from "@/components/golive-checklist";
import { StripeStatusWidget } from "@/components/stripe-status-widget";
import { WebhookActivityPanel } from "@/components/webhook-activity-panel";
import { TeamAuditPanel } from "@/components/team-audit-panel";


export const Route = createFileRoute("/_authenticated/admin/")({
  head: () => ({
    meta: [
      { title: "Admin overview — Quibblies" },
      {
        name: "description",
        content:
          "Quibblies staff overview: payment provider status, live webhook testing and shortcuts to the moderation, finance and email tools.",
      },
      { property: "og:title", content: "Admin overview — Quibblies" },
      {
        property: "og:description",
        content: "Payment provider status and staff tool shortcuts for Quibblies administrators.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: AdminOverviewPage,
});

const LINKS: Array<{ to: string; label: string }> = [
  { to: "/moderation", label: "Publication moderation" },
  { to: "/admin/team", label: "Team" },
  { to: "/admin/members/pending", label: "Pending members" },
  { to: "/admin/moderation/images", label: "Image moderation" },
  { to: "/admin/moderation/age-verification", label: "Age verification" },
  { to: "/admin/finance", label: "Finance" },
  { to: "/admin/tip-actions", label: "Tip actions" },
  { to: "/admin/tip-replay", label: "Webhook replay" },
  { to: "/admin/webhook-health", label: "Webhook health" },
  { to: "/admin/email-delivery", label: "Email delivery" },
  { to: "/admin/audit", label: "Audit log" },
  { to: "/admin/ops-settings", label: "Alert thresholds" },
];

function AdminOverviewPage() {
  return (
    <div className="mx-auto max-w-5xl px-4 py-12 sm:px-6">
      <SectionHeading
        title="Admin overview"
        description="Payment provider readiness and quick access to the staff tools."
      />

      <div className="mt-8">
        <h2 className="mb-3 text-sm font-semibold tracking-wide text-muted-foreground uppercase">
          Payments
        </h2>
        <StripeStatusWidget />
      </div>

      <div className="mt-10">
        <h2 className="mb-3 text-sm font-semibold tracking-wide text-muted-foreground uppercase">
          Stripe go-live checklist
        </h2>
        <GoLiveChecklist />
      </div>

      <div className="mt-10">
        <h2 className="mb-3 text-sm font-semibold tracking-wide text-muted-foreground uppercase">
          Webhook processing
        </h2>
        <WebhookActivityPanel />
      </div>



      <div className="mt-10">
        <h2 className="mb-3 text-sm font-semibold tracking-wide text-muted-foreground uppercase">
          Team changes
        </h2>
        <TeamAuditPanel />
      </div>

      <div className="mt-10">
        <h2 className="mb-3 text-sm font-semibold tracking-wide text-muted-foreground uppercase">
          Staff tools
        </h2>
        <div className="flex flex-wrap gap-2">
          {LINKS.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className="rounded-full border border-border/60 bg-surface/50 px-4 py-2 text-sm text-foreground hover:border-primary/50"
            >
              {link.label}
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
